import { Component } from '@theme/component'
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events'
import { morph } from '@theme/morph'
import { cartPerformance } from '@theme/performance'
import { ProductFormConfig } from '@theme/product-form-config'
import { fetchConfig, parseItemCountFromSectionsHTML } from '@theme/utils'

export class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion']
  #abortController = null

  #timeout

  #successTimeout

  connectedCallback() {
    super.connectedCallback()
    this.#abortController?.abort()
    this.#abortController = new AbortController()
    const { signal } = this.#abortController
    const target = this.closest('.shopify-section, dialog, product-card')
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal
    })
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.#abortController?.abort()
    this.#abortController = null
    this.#clearErrorTimeout()
    this.#clearSuccessTimeout()
  }

  async handleSubmit(event) {
    event.preventDefault()
    this.#clearErrorTimeout()
    if (this.#isSubmitDisabled()) return

    const form = this.#getProductForm()
    await this.#submitProductForm(form, event)
  }

  #getProductForm() {
    const form = this.querySelector('form')
    if (!(form instanceof HTMLFormElement)) {
      throw new Error(
        `Product form missing for "${this.dataset.productId ?? 'unknown'}"; expected HTMLFormElement`
      )
    }
    return form
  }

  async #submitProductForm(form, event) {
    const formData = this.#buildCartFormData(form)
    const requestedQuantity = this.#resolveRequestedQuantity(formData)

    try {
      const response = await this.#addCartLine(formData)
      if (response.status) {
        this.#handleCartErrorResponse(form, response)
        return
      }
      this.#handleCartSuccessResponse(formData, response, requestedQuantity)
    } catch (error) {
      this.#handleCartRequestError(form, error)
    } finally {
      cartPerformance.measureFromEvent('add:user-action', event)
    }
  }

  #buildCartFormData(form) {
    const formData = new FormData(form)
    const sectionIds = this.#getCartItemSectionIds()
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','))
    }
    return formData
  }

  #resolveRequestedQuantity(formData) {
    const requestedQuantity = Number(
      formData.get('quantity') ?? this.dataset.quantityDefault ?? 0
    )
    return Number.isNaN(requestedQuantity) ? 0 : requestedQuantity
  }

  #getCartItemSectionIds() {
    const sectionIds = []
    for (const item of document.querySelectorAll('cart-items')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId)
      }
    }
    return sectionIds
  }

  async #addCartLine(formData) {
    const fetchCfg = fetchConfig('javascript', { body: formData })
    const response = await fetch(Theme.routes.cart_add_url, { ...fetchCfg })
    const cartResponse = await response.json()
    if (response.ok || cartResponse.status) return cartResponse

    return {
      ...cartResponse,
      description: response.statusText,
      message: this.#getCartErrorMessage(),
      status: response.status
    }
  }

  #handleCartErrorResponse(form, response) {
    const message = response.message || this.#getCartErrorMessage()
    this.dispatchEvent(
      new CartErrorEvent(
        form.getAttribute('id') || '',
        message,
        response.description,
        response.errors
      )
    )
    this.#showCartError(message)
    this.#dispatchCartAddError(response)
  }

  #handleCartSuccessResponse(formData, response, requestedQuantity) {
    const id = formData.get('id')
    if (!id) {
      throw new Error(
        `Form variant ID missing for "${this.dataset.productId ?? 'unknown'}"; expected form field "id"`
      )
    }

    this.#hideCartError()
    this.#announceAddToCartSuccess()
    const itemCount = this.#resolveItemCount(response, requestedQuantity)
    this.#dispatchCartAddSuccess(response, id.toString(), itemCount)
  }

  #dispatchCartAddSuccess(response, sourceId, itemCount) {
    this.dispatchEvent(
      new CartAddEvent(response, sourceId, {
        animationDuration: ProductFormConfig.animationDuration,
        itemCount,
        productId: this.dataset.productId,
        sections: response.sections,
        source: 'product-form'
      })
    )
  }

  #handleCartRequestError(form, error) {
    const description = error instanceof Error ? error.message : String(error)
    const response = {
      description,
      message: this.#getCartErrorMessage(),
      status: 'error'
    }
    this.#handleCartErrorResponse(form, response)
  }

  #isSubmitDisabled() {
    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton
    return button instanceof HTMLButtonElement && button.disabled
  }

  #showCartError(message) {
    this.#setLiveRegionText(message)
    const { addToCartTextError } = this.refs
    if (!(addToCartTextError instanceof HTMLElement)) return

    addToCartTextError.classList.remove(ProductFormConfig.classes.hidden)
    this.#setErrorMessage(addToCartTextError, message)
    this.#timeout = setTimeout(() => {
      this.#hideCartError()
      this.#timeout = undefined
    }, ProductFormConfig.errorTimeout)
  }

  #hideCartError() {
    const { addToCartTextError } = this.refs
    addToCartTextError?.classList.add(ProductFormConfig.classes.hidden)
    addToCartTextError?.removeAttribute('aria-live')
    this.#clearLiveRegionText()
  }

  #setErrorMessage(errorElement, message) {
    const textNode = this.#getErrorTextNode(errorElement)
    textNode.textContent = message
  }

  #getErrorTextNode(errorElement) {
    for (const node of errorElement.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        return node
      }
    }

    const textNode = document.createTextNode('')
    errorElement.appendChild(textNode)
    return textNode
  }

  #announceAddToCartSuccess() {
    const addToCartButton =
      this.refs.addToCartButtonContainer?.refs.addToCartButton
    if (!(addToCartButton instanceof HTMLElement)) return

    const addedTextElement = addToCartButton.querySelector(
      ProductFormConfig.classes.addedText
    )
    const addedText =
      addedTextElement?.textContent?.trim() || Theme.translations.added
    this.#setLiveRegionText(addedText)
    this.#clearSuccessTimeout()
    this.#successTimeout = setTimeout(() => {
      this.#clearLiveRegionText()
      this.#successTimeout = undefined
    }, ProductFormConfig.successTimeout)
  }

  #dispatchCartAddError(response) {
    this.dispatchEvent(
      new CartAddEvent(response, this.id, {
        animationDuration: ProductFormConfig.animationDuration,
        didError: true,
        itemCount: this.#getCurrentItemCount(),
        productId: this.dataset.productId,
        source: 'product-form'
      })
    )
  }

  #clearErrorTimeout() {
    if (!this.#timeout) return
    clearTimeout(this.#timeout)
    this.#timeout = undefined
  }

  #clearSuccessTimeout() {
    if (!this.#successTimeout) return
    clearTimeout(this.#successTimeout)
    this.#successTimeout = undefined
  }

  #getCartErrorMessage() {
    return Theme.translations.cart_error || 'Unable to add item to cart.'
  }

  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion
    liveRegion.textContent = text
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion
    liveRegion.textContent = ''
  }

  #resolveItemCount(response, requestedQuantity) {
    if (response && typeof response.item_count === 'number') {
      return response.item_count
    }

    const sectionCount = parseItemCountFromSectionsHTML(response?.sections)
    if (typeof sectionCount === 'number') {
      return sectionCount
    }

    const currentCount = this.#getCurrentItemCount()
    return currentCount + requestedQuantity
  }

  #getCurrentItemCount() {
    const countEl = document.querySelector('[ref="cartItemCount"]')
    if (countEl) {
      const count = Number.parseInt(countEl.textContent ?? '', 10)
      if (!Number.isNaN(count)) {
        return count
      }
    }

    try {
      const stored = sessionStorage.getItem('cart-count')
      if (stored) {
        const { value } = JSON.parse(stored)
        const count = Number.parseInt(value, 10)
        if (!Number.isNaN(count)) {
          return count
        }
      }
    } catch {
      /* empty */
    }

    return 0
  }

  #onVariantUpdate = (event) => {
    if (!this.#shouldHandleVariantUpdate(event)) {
      return
    }

    const resource = event.detail.resource
    const { variantId, addToCartButtonContainer } = this.refs
    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton
    if (!currentAddToCartButton) {
      return
    }

    this.#syncAddToCartAvailability(resource, addToCartButtonContainer)
    this.#morphAddToCartButton(currentAddToCartButton, event.detail.data.html)
    variantId.value = resource?.id ?? ''
    this.#syncVariantMedia(resource, addToCartButtonContainer)
  }

  #shouldHandleVariantUpdate(event) {
    const data = event.detail?.data
    if (!data) return false
    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id
      return true
    }
    return data.productId === this.dataset.productId
  }

  #syncAddToCartAvailability(resource, addToCartButtonContainer) {
    if (resource == null || resource.available === false) {
      addToCartButtonContainer?.disable()
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        'hidden',
        'true'
      )
      return
    }

    addToCartButtonContainer?.enable()
    this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden')
  }

  #morphAddToCartButton(currentAddToCartButton, html) {
    const newAddToCartButton = html?.querySelector('[ref="addToCartButton"]')
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton)
    }
  }

  #syncVariantMedia(resource, addToCartButtonContainer) {
    const productVariantMedia = resource?.featured_media?.preview_image?.src
    if (!productVariantMedia) return

    addToCartButtonContainer?.setAttribute(
      'data-product-variant-media',
      `${productVariantMedia}&width=100`
    )
  }

  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable()
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductFormComponent)
}
